//Komponent odpowiedzialny za zarządzaniem zespołami ( tworzenie, edytowanie kasowanie )

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Edit, Trash2, X, ChevronDown, ChevronUp } from "lucide-react";
import { useLocation } from 'react-router-dom';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const TeamManager = () => {
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [newTeam, setNewTeam] = useState({
    name: '',
    team_leader: '',
    person_responsible_for_preparation_of_work_place: '',
    team: [''],
  });

  const location = useLocation();
  const initialEditingTeam = location.state?.editingTeam || null;
  const [editingTeam, setEditingTeam] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [weekActivityCount, setWeekActivityCount] = useState(4); // Domyślnie 4 tygodnie
  const [weekActivities, setWeekActivities] = useState([]); // Lista aktywnych tygodni
  const [currentWeek, setCurrentWeek] = useState(null);

  const handleWeekActivityChange = ({ year, week }, isSelected) => {
    const updatedWeekActivities = isSelected
      ? [...weekActivities, { year, week }]
      : weekActivities.filter(
        (w) => !(w.year === year && w.week === week)
      );

    setWeekActivities(updatedWeekActivities);

    // Logowanie do konsoli
    console.log('Selected weeks:', updatedWeekActivities);
  };

  // Funkcja do określania aktualnego tygodnia
  const getWeekNumber = (date) => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay()) / 7);
  };

  const getWeeksInYear = (year) => {
    const firstDayOfYear = new Date(year, 0, 1); // 1 stycznia
    const lastDayOfYear = new Date(year, 11, 31); // 31 grudnia

    // Oblicz, ile dni w roku upływa od pierwszej niedzieli
    const daysInYear = (lastDayOfYear - firstDayOfYear) / 86400000 + 1;

    // Dzień tygodnia 1 stycznia (0 = niedziela, 1 = poniedziałek, ..., 6 = sobota)
    const startDayOfWeek = firstDayOfYear.getDay();

    // Oblicz, ile dni potrzeba, aby osiągnąć pierwszą niedzielę
    const daysToFirstSunday = startDayOfWeek === 0 ? 0 : 7 - startDayOfWeek;

    // Oblicz, ile pełnych tygodni mieści się od pierwszej niedzieli do końca roku
    const fullWeeks = Math.ceil((daysInYear - daysToFirstSunday) / 7);

    return fullWeeks;
  };

  const currentYear = new Date().getFullYear();

  const generateWeeks = () => {
    const weeks = [];
    let year = currentYear;
    let week = currentWeek;

    for (let i = 0; i < weekActivityCount; i++) {
      weeks.push({ year, week });

      // Liczba tygodni w bieżącym roku
      const maxWeeks = getWeeksInYear(year);

      // Jeśli osiągniemy ostatni tydzień roku, przechodzimy do nowego roku
      if (week === maxWeeks) {
        week = 1;
        year += 1;
      } else {
        week += 1;
      }
    }

    return weeks;
  };

  // Pobieranie danych przy otwarciu modala
  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Ustawienie aktualnego tygodnia przy ładowaniu komponentu
    const today = new Date();
    setCurrentWeek(getWeekNumber(today));
  }, []);



  useEffect(() => {
    if (editingTeam) {
      const normalizedNewTeam = prepareTeamArray(
        newTeam.team,
        newTeam.team_leader,
        newTeam.person_responsible_for_preparation_of_work_place
      );

      const normalizedEditingTeam = prepareTeamArray(
        editingTeam.team,
        editingTeam.team_leader,
        editingTeam.person_responsible_for_preparation_of_work_place
      );

      console.log('Normalized newTeam:', normalizedNewTeam);
      console.log('Normalized editingTeam:', normalizedEditingTeam);

      const hasChanged =
        newTeam.name !== editingTeam.name ||
        !arraysEqual(normalizedNewTeam, normalizedEditingTeam);

      if (hasChanged) {
        console.log('Detected changes, switching to create mode.');
        setEditingTeam(null); // Przełącz na tryb `create`
      }
    }
  }, [newTeam, editingTeam]);

  // Funkcja do inicjalizacji danych zespołu w formularzu
  const handleTeamClick = useCallback((team) => {
    if (!team) return;

    const filteredMembers = team.team.filter(
      (member) =>
        member !== team.team_leader &&
        member !== team.person_responsible_for_preparation_of_work_place
    );

    setEditingTeam(team);
    setNewTeam({
      name: team.name || "",
      team_leader: team.team_leader || "",
      person_responsible_for_preparation_of_work_place:
        team.person_responsible_for_preparation_of_work_place || "",
      team: [...filteredMembers, ""],
    });

    setWeekActivities(
      Object.entries(team.active_periods || {}).flatMap(([year, weeks]) =>
        weeks.map((week) => ({ year: parseInt(year, 10), week }))
      )
    );
  }, []);

  useEffect(() => {
    console.log('Initial Editing Team:', initialEditingTeam);
    handleTeamClick(initialEditingTeam);
  }, [initialEditingTeam, handleTeamClick]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('access_token');
      const [teamsResponse, usersResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/teams/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE_URL}/users/`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setTeams(teamsResponse.data);
      setUsers(usersResponse.data.filter(user => user.is_active)); // Pobierz tylko aktywnych użytkowników
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Unable to fetch data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddOrUpdateTeam = async () => {
    const token = localStorage.getItem('access_token');
    try {
      // Scal wszystkich członków zespołu
      const allTeamMembers = [
        newTeam.team_leader,
        newTeam.person_responsible_for_preparation_of_work_place,
        ...newTeam.team.filter((member) => member), // Usuń puste wartości
      ];

      const newPayload = {
        name: newTeam.name,
        team_leader: newTeam.team_leader,
        person_responsible_for_preparation_of_work_place: newTeam.person_responsible_for_preparation_of_work_place,
        team: allTeamMembers,
        active_periods: weekActivities.reduce((acc, { year, week }) => {
          acc[year] = acc[year] || [];
          acc[year].push(week);
          return acc;
        }, {}),
      };

      if (editingTeam) {
        // Sprawdź, czy dane zespołu zmieniły się w sposób wymagający "create"
        const isFullCreateRequired =
          editingTeam.name !== newPayload.name ||
          editingTeam.team_leader !== newPayload.team_leader ||
          editingTeam.person_responsible_for_preparation_of_work_place !==
          newPayload.person_responsible_for_preparation_of_work_place ||
          !arraysEqual(editingTeam.team, newPayload.team); // Funkcja porównująca tablice

        if (isFullCreateRequired) {
          // Tworzymy nowy zespół
          const response = await axios.post(`${API_BASE_URL}/teams/`, newPayload, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setTeams([...teams, response.data]); // Dodaj nowy zespół
        } else {
          // Aktualizujemy pełne dane zespołu, nie tylko `active_periods`
          const response = await axios.put(
            `${API_BASE_URL}/teams/${editingTeam.id}/`,
            newPayload, // Pełny payload
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          setTeams(
            teams.map((team) =>
              team.id === editingTeam.id ? response.data : team
            )
          ); // Zaktualizuj dane zespołu
        }
        setEditingTeam(null); // Wyczyszczenie edytowanego zespołu
      } else {
        // Tworzenie nowego zespołu
        const response = await axios.post(`${API_BASE_URL}/teams/`, newPayload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTeams([...teams, response.data]);
      }

      // Resetuj stan formularza
      setNewTeam({
        name: '',
        team_leader: '',
        person_responsible_for_preparation_of_work_place: '',
        team: [''], // Zaczynamy od jednego pustego pola
      });
      setWeekActivities([]); // Resetuj aktywne tygodnie
      setWeekActivityCount(4);
      setError('');
    } catch (err) {
      console.error('Error saving team:', err);
      setError('Unable to save team. Please try again later.');
    }
  };

  const prepareTeamArray = (team, leader, preparer) => {
    // Normalizacja: scal tablicę i usuń duplikaty
    const combinedTeam = [
      leader,
      preparer,
      ...team.filter((member) => member !== ''), // Usuń puste wartości
    ];

    // Usuwamy duplikaty
    const uniqueTeam = [...new Set(combinedTeam.map(Number))];

    return uniqueTeam;
  };

  const arraysEqual = (arr1, arr2) => {
    if (arr1.length !== arr2.length) return false;

    const sortedArr1 = [...arr1].sort((a, b) => a - b);
    const sortedArr2 = [...arr2].sort((a, b) => a - b);

    return sortedArr1.every((value, index) => value === sortedArr2[index]);
  };


  const handleDeleteTeam = async (teamId) => {
    const token = localStorage.getItem('access_token');
    try {
      await axios.delete(`${API_BASE_URL}/teams/${teamId}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTeams(teams.filter(team => team.id !== teamId));
      setError('');
    } catch (err) {
      console.error('Error deleting team:', err);
      setError('Unable to delete team. Please try again later.');
    }
  };

  const handleTeamMemberChange = (e, index) => {
    let updatedTeam = [...newTeam.team];

    // Jeśli pole zostało odznaczone, usuń je
    if (!e.target.value) {
      updatedTeam.splice(index, 1);
    } else {
      // Jeśli wybrano wartość, zaktualizuj pole
      updatedTeam[index] = e.target.value;

      // Dodaj nowe pole, jeśli to ostatnie ma wartość
      if (index === updatedTeam.length - 1) {
        updatedTeam.push('');
      }
    }

    setNewTeam({ ...newTeam, team: updatedTeam });
  };

  const [searchQuery, setSearchQuery] = useState('');

  // Search states for select dropdowns
  const [supervisorSearch, setSupervisorSearch] = useState('');
  const [preparerSearch, setPreparerSearch] = useState('');
  const [otherMembersSearches, setOtherMembersSearches] = useState([]);

  // Expansion states for collapsible selects
  const [supervisorExpanded, setSupervisorExpanded] = useState(false);
  const [preparerExpanded, setPreparerExpanded] = useState(false);
  const [otherMembersExpanded, setOtherMembersExpanded] = useState([]);

  // Helper function to filter users based on search query
  const filterUsersBySearch = (usersList, searchQuery) => {
    if (!searchQuery) return usersList;
    const searchLower = searchQuery.toLowerCase();
    return usersList.filter(user =>
      user.first_name.toLowerCase().includes(searchLower) ||
      user.last_name.toLowerCase().includes(searchLower) ||
      (user.email && user.email.toLowerCase().includes(searchLower)) ||
      (user.username && user.username.toLowerCase().includes(searchLower))
    );
  };

  const filteredTeams = teams.filter((team) => {
    const supervisor = users.find((user) => user.id === team.team_leader);
    const preparer = users.find((user) => user.id === team.person_responsible_for_preparation_of_work_place);
    const members = team.team
      .map((memberId) => {
        const member = users.find((user) => user.id === memberId);
        return member ? `${member.first_name} ${member.last_name}` : '';
      })
      .join(' ');
    const activities = Object.entries(team.active_periods)
      .flatMap(([year, weeks]) => weeks.map((week) => `${week}/${year}`))
      .join(' ');

    const searchLower = searchQuery.toLowerCase();
    return (
      team.name.toLowerCase().includes(searchLower) ||
      (supervisor && `${supervisor.first_name} ${supervisor.last_name}`.toLowerCase().includes(searchLower)) ||
      (preparer && `${preparer.first_name} ${preparer.last_name}`.toLowerCase().includes(searchLower)) ||
      members.toLowerCase().includes(searchLower) ||
      activities.toLowerCase().includes(searchLower)
    );
  });

  return (
    <div className="min-h-screen bg-steel-100">
      {/* Nagłówek */}
      <header className="bg-gradient-to-r from-brand-50 to-steel-50 shadow p-4 border-b border-steel-200">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-semibold text-slate-900">Manage Teams</h1>
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded-xl border-2 border-steel-300 bg-white text-slate-700 hover:bg-steel-50 hover:border-brand-300 transition-colors"
          >
            Go Back
          </Button>
        </div>
      </header>

      {/* Główna zawartość */}
      <main className="container mx-auto py-6 px-4">
        {/* Sekcja ładowania i błędów */}
        {loading && <p className="text-center text-slate-500">Loading data...</p>}
        {error && <p className="text-center text-red-600">{error}</p>}

        <div className="grid grid-cols-3 gap-6">
          {/* Formularz tworzenia / edytowania zespołów */}
          <div className="col-span-1 bg-white p-4 rounded-xl shadow border border-steel-200">
            <h2 className="text-lg font-semibold mb-4 text-slate-900">
              {editingTeam ? 'Edit Team' : 'Create New Team'}
            </h2>
            <div className="grid gap-4">
              {/* Team Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700">Team Name</label>
                <input
                  type="text"
                  value={newTeam.name}
                  onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                  placeholder="Enter team name"
                  className="w-full p-2 border border-steel-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>

              {/* Supervisor */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Supervisor</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 border border-steel-300 rounded-xl">
                    {/* Clickable header */}
                    <button
                      type="button"
                      onClick={() => setSupervisorExpanded(!supervisorExpanded)}
                      className="w-full px-3 py-2 flex items-center justify-between hover:bg-steel-50 transition-colors"
                    >
                      <span className="text-sm text-slate-700 truncate">
                        {newTeam.team_leader ? (
                          (() => {
                            const selected = users.find(u => u.id === parseInt(newTeam.team_leader));
                            return selected ? `${selected.first_name} ${selected.last_name}` : 'Select supervisor...';
                          })()
                        ) : (
                          'Select supervisor...'
                        )}
                      </span>
                      {supervisorExpanded ? (
                        <ChevronUp className="h-4 w-4 text-slate-500 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-slate-500 flex-shrink-0" />
                      )}
                    </button>

                    {/* Expandable content */}
                    {supervisorExpanded && (
                      <div className="border-t border-steel-200">
                        {/* Search input */}
                        <div className="p-2 border-b border-steel-200">
                          <input
                            type="text"
                            placeholder="Search by name, email, or username..."
                            className="w-full px-2 py-1 text-sm border border-steel-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-brand-500"
                            value={supervisorSearch}
                            onChange={(e) => setSupervisorSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        {/* Options list */}
                        <div className="max-h-48 overflow-y-auto p-2 space-y-1">
                          {filterUsersBySearch(
                            users.filter(
                              (user) =>
                                user.is_active &&
                                !user.is_company_admin &&
                                (user.id === parseInt(newTeam.team_leader) ||
                                  (user.id !== parseInt(newTeam.person_responsible_for_preparation_of_work_place) &&
                                    !newTeam.team.includes(user.id.toString())))
                            ),
                            supervisorSearch
                          ).map((user) => (
                            <div
                              key={user.id}
                              onClick={() => {
                                setNewTeam({ ...newTeam, team_leader: user.id.toString() });
                                setSupervisorExpanded(false);
                                setSupervisorSearch('');
                              }}
                              className={`px-2 py-1.5 text-sm cursor-pointer rounded hover:bg-steel-100 ${
                                parseInt(newTeam.team_leader) === user.id ? 'bg-brand-50 text-brand-700' : ''
                              }`}
                            >
                              {user.first_name} {user.last_name}
                              <span className="text-slate-500 text-xs ml-1">({user.username})</span>
                            </div>
                          ))}
                          {filterUsersBySearch(
                            users.filter(
                              (user) =>
                                user.is_active &&
                                !user.is_company_admin &&
                                (user.id === parseInt(newTeam.team_leader) ||
                                  (user.id !== parseInt(newTeam.person_responsible_for_preparation_of_work_place) &&
                                    !newTeam.team.includes(user.id.toString())))
                            ),
                            supervisorSearch
                          ).length === 0 && (
                            <div className="text-sm text-slate-500 py-2">No users found</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setNewTeam({ ...newTeam, team_leader: '' })}
                    className="text-red-600 hover:text-red-800 flex-shrink-0"
                    title="Remove Supervisor"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Preparer */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Preparer</label>
                <div className="flex items-center gap-2">
                  <div className="flex-1 border border-steel-300 rounded-xl">
                    {/* Clickable header */}
                    <button
                      type="button"
                      onClick={() => setPreparerExpanded(!preparerExpanded)}
                      className="w-full px-3 py-2 flex items-center justify-between hover:bg-steel-50 transition-colors"
                    >
                      <span className="text-sm text-slate-700 truncate">
                        {newTeam.person_responsible_for_preparation_of_work_place ? (
                          (() => {
                            const selected = users.find(u => u.id === parseInt(newTeam.person_responsible_for_preparation_of_work_place));
                            return selected ? `${selected.first_name} ${selected.last_name}` : 'Select preparer...';
                          })()
                        ) : (
                          'Select preparer...'
                        )}
                      </span>
                      {preparerExpanded ? (
                        <ChevronUp className="h-4 w-4 text-slate-500 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-slate-500 flex-shrink-0" />
                      )}
                    </button>

                    {/* Expandable content */}
                    {preparerExpanded && (
                      <div className="border-t border-steel-200">
                        {/* Search input */}
                        <div className="p-2 border-b border-steel-200">
                          <input
                            type="text"
                            placeholder="Search by name, email, or username..."
                            className="w-full px-2 py-1 text-sm border border-steel-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-brand-500"
                            value={preparerSearch}
                            onChange={(e) => setPreparerSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        {/* Options list */}
                        <div className="max-h-48 overflow-y-auto p-2 space-y-1">
                          {filterUsersBySearch(
                            users.filter(
                              (user) =>
                                user.is_active &&
                                !user.is_company_admin &&
                                (user.id === parseInt(newTeam.person_responsible_for_preparation_of_work_place) ||
                                  (user.id !== parseInt(newTeam.team_leader) &&
                                    !newTeam.team.includes(user.id.toString())))
                            ),
                            preparerSearch
                          ).map((user) => (
                            <div
                              key={user.id}
                              onClick={() => {
                                setNewTeam({ ...newTeam, person_responsible_for_preparation_of_work_place: user.id.toString() });
                                setPreparerExpanded(false);
                                setPreparerSearch('');
                              }}
                              className={`px-2 py-1.5 text-sm cursor-pointer rounded hover:bg-steel-100 ${
                                parseInt(newTeam.person_responsible_for_preparation_of_work_place) === user.id ? 'bg-brand-50 text-brand-700' : ''
                              }`}
                            >
                              {user.first_name} {user.last_name}
                              <span className="text-slate-500 text-xs ml-1">({user.username})</span>
                            </div>
                          ))}
                          {filterUsersBySearch(
                            users.filter(
                              (user) =>
                                user.is_active &&
                                !user.is_company_admin &&
                                (user.id === parseInt(newTeam.person_responsible_for_preparation_of_work_place) ||
                                  (user.id !== parseInt(newTeam.team_leader) &&
                                    !newTeam.team.includes(user.id.toString())))
                            ),
                            preparerSearch
                          ).length === 0 && (
                            <div className="text-sm text-slate-500 py-2">No users found</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setNewTeam({ ...newTeam, person_responsible_for_preparation_of_work_place: '' })}
                    className="text-red-600 hover:text-red-800 flex-shrink-0"
                    title="Remove Preparer"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Other Members */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Other Members</label>
                {newTeam.team.map((member, index) => (
                  <div key={index} className="mb-2 flex items-center gap-2">
                    <div className="flex-1 border border-steel-300 rounded-xl">
                      {/* Clickable header */}
                      <button
                        type="button"
                        onClick={() => {
                          const newExpanded = [...otherMembersExpanded];
                          newExpanded[index] = !newExpanded[index];
                          setOtherMembersExpanded(newExpanded);
                        }}
                        className="w-full px-3 py-2 flex items-center justify-between hover:bg-steel-50 transition-colors"
                      >
                        <span className="text-sm text-slate-700 truncate">
                          {member ? (
                            (() => {
                              const selected = users.find(u => u.id === parseInt(member));
                              return selected ? `${selected.first_name} ${selected.last_name}` : 'Choose a team member...';
                            })()
                          ) : (
                            'Choose a team member...'
                          )}
                        </span>
                        {otherMembersExpanded[index] ? (
                          <ChevronUp className="h-4 w-4 text-slate-500 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-slate-500 flex-shrink-0" />
                        )}
                      </button>

                      {/* Expandable content */}
                      {otherMembersExpanded[index] && (
                        <div className="border-t border-steel-200">
                          {/* Search input */}
                          <div className="p-2 border-b border-steel-200">
                            <input
                              type="text"
                              placeholder="Search by name, email, or username..."
                              className="w-full px-2 py-1 text-sm border border-steel-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-brand-500"
                              value={otherMembersSearches[index] || ''}
                              onChange={(e) => {
                                const newSearches = [...otherMembersSearches];
                                newSearches[index] = e.target.value;
                                setOtherMembersSearches(newSearches);
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          {/* Options list */}
                          <div className="max-h-48 overflow-y-auto p-2 space-y-1">
                            {filterUsersBySearch(
                              users.filter(
                                (user) =>
                                  user.is_active &&
                                  !user.is_company_admin &&
                                  (user.id === parseInt(member) ||
                                    (user.id !== parseInt(newTeam.team_leader) &&
                                      user.id !== parseInt(newTeam.person_responsible_for_preparation_of_work_place) &&
                                      !newTeam.team.includes(user.id.toString())))
                              ),
                              otherMembersSearches[index] || ''
                            ).map((user) => (
                              <div
                                key={user.id}
                                onClick={() => {
                                  handleTeamMemberChange({ target: { value: user.id.toString() } }, index);
                                  const newExpanded = [...otherMembersExpanded];
                                  newExpanded[index] = false;
                                  setOtherMembersExpanded(newExpanded);
                                  const newSearches = [...otherMembersSearches];
                                  newSearches[index] = '';
                                  setOtherMembersSearches(newSearches);
                                }}
                                className={`px-2 py-1.5 text-sm cursor-pointer rounded hover:bg-steel-100 ${
                                  parseInt(member) === user.id ? 'bg-brand-50 text-brand-700' : ''
                                }`}
                              >
                                {user.first_name} {user.last_name}
                                <span className="text-slate-500 text-xs ml-1">({user.username})</span>
                              </div>
                            ))}
                            {filterUsersBySearch(
                              users.filter(
                                (user) =>
                                  user.is_active &&
                                  !user.is_company_admin &&
                                  (user.id === parseInt(member) ||
                                    (user.id !== parseInt(newTeam.team_leader) &&
                                      user.id !== parseInt(newTeam.person_responsible_for_preparation_of_work_place) &&
                                      !newTeam.team.includes(user.id.toString())))
                              ),
                              otherMembersSearches[index] || ''
                            ).length === 0 && (
                              <div className="text-sm text-slate-500 py-2">No users found</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        const updatedTeam = [...newTeam.team];
                        if (newTeam.team.length > 1) {
                          updatedTeam.splice(index, 1);
                        } else {
                          updatedTeam[index] = '';
                        }
                        setNewTeam({ ...newTeam, team: updatedTeam });
                      }}
                      className="text-red-600 hover:text-red-800 flex-shrink-0"
                      title="Remove Member"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Sekcja Week Activity */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1 mt-2">Week Activity</label>
              <p className="text-sm text-slate-500 mb-2">
                Select the weeks of the team&apos;s activity{' '}
                {currentWeek !== null && <span>(current week: {currentWeek})</span>}
              </p>
              <div className="grid grid-cols-4 gap-2">
                {generateWeeks().map(({ year, week }) => (
                  <label key={`${year}-${week}`} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={weekActivities.some(
                        (w) => w.year === year && w.week === week
                      )}
                      onChange={(e) =>
                        handleWeekActivityChange({ year, week }, e.target.checked)
                      }
                      className="form-checkbox h-4 w-4 text-brand-500 rounded focus:ring-brand-500"
                    />
                    <span className="text-sm text-slate-700">
                      {week}/{year}
                    </span>
                  </label>
                ))}
              </div>
              {weekActivityCount < 100 && (
                <button
                  type="button"
                  onClick={() => setWeekActivityCount((prev) => Math.min(prev + 4, 100))}
                  className="mt-2 text-brand-600 hover:underline text-sm"
                >
                  Show more weeks
                </button>
              )}
            </div>

            <div className="mt-4 flex justify-between gap-2">
              {editingTeam && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditingTeam(null);
                    setNewTeam({
                      name: '',
                      team_leader: '',
                      person_responsible_for_preparation_of_work_place: '',
                      team: [''],
                    });
                    setWeekActivities([]); // Wyczyszczenie tygodni aktywności
                    setWeekActivityCount(4);
                  }}
                  className="w-1/2"
                >
                  Clear
                </Button>
              )}
              <Button
                onClick={handleAddOrUpdateTeam}
                className={`bg-brand-500 hover:bg-brand-600 text-white rounded-xl shadow-lg shadow-brand-500/20 ${editingTeam ? 'w-1/2' : 'w-full'}`}
              >
                {editingTeam ? 'Update Team' : 'Add Team'}
              </Button>
            </div>
          </div>

          {/* Lista zespołów */}
          <div className="col-span-2 bg-white p-4 rounded-xl shadow border border-steel-200">
            {/* Nagłówek z wyszukiwaniem i przyciskiem "Show all" */}
            <div className="flex items-center justify-between mb-4">
              {/* Nagłówek po lewej stronie */}
              <h2 className="text-lg font-semibold text-slate-900">Teams</h2>

              {/* Pole wyszukiwania na środku */}
              <div className="flex-grow mx-4">
                <input
                  type="text"
                  placeholder="Search teams by name, supervisor, preparer, or members"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full p-2 text-sm border border-steel-300 rounded-full focus:outline-none focus:ring-1 focus:ring-brand-500"
                />
              </div>


            </div>
            <div
              className="grid grid-cols-2 gap-3 overflow-y-auto"
              style={{ maxHeight: '740px' }}
            >
              {filteredTeams.map((team) => {
                const supervisor = users.find((user) => user.id === team.team_leader);
                const preparer = users.find((user) => user.id === team.person_responsible_for_preparation_of_work_place);
                const otherMembers = team.team
                  ?.filter((memberId) => memberId !== team.team_leader && memberId !== team.person_responsible_for_preparation_of_work_place)
                  .map((memberId) => users.find((u) => u.id === memberId))
                  .filter(Boolean) || [];

                const UserAvatar = ({ user, size = 'sm' }) => {
                  const sizeClasses = size === 'sm' ? 'w-6 h-6 text-[10px]' : 'w-7 h-7 text-xs';
                  if (user?.profile_picture) {
                    return <img src={user.profile_picture} alt="" className={`${sizeClasses} rounded-full object-cover border border-steel-200`} />;
                  }
                  const initials = user ? `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}` : '?';
                  return (
                    <div className={`${sizeClasses} rounded-full bg-steel-200 text-slate-600 flex items-center justify-center font-medium`}>
                      {initials}
                    </div>
                  );
                };

                return (
                  <div
                    key={team.id}
                    className={`p-3 bg-white border-2 rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md hover:border-brand-300 ${
                      editingTeam?.id === team.id
                        ? 'border-brand-500 bg-brand-50 shadow-md shadow-brand-500/10'
                        : 'border-steel-200 shadow-sm'
                    }`}
                    onClick={() => handleTeamClick(team)}
                  >
                    {/* Header */}
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-sm text-slate-900">{team.name}</h3>
                      {editingTeam?.id === team.id && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-brand-500 text-white rounded">Editing</span>
                      )}
                    </div>

                    {/* Team members */}
                    <div className="space-y-1.5">
                      {/* Supervisor */}
                      <div className="flex items-center gap-2">
                        <UserAvatar user={supervisor} />
                        <div className="flex-1 min-w-0">
                          <span className="text-[10px] text-slate-500 font-medium">Supervisor</span>
                          <p className="text-xs text-slate-700 truncate">
                            {supervisor ? `${supervisor.first_name} ${supervisor.last_name}` : '-'}
                          </p>
                        </div>
                      </div>

                      {/* Preparer */}
                      <div className="flex items-center gap-2">
                        <UserAvatar user={preparer} />
                        <div className="flex-1 min-w-0">
                          <span className="text-[10px] text-slate-500 font-medium">Preparer</span>
                          <p className="text-xs text-slate-700 truncate">
                            {preparer ? `${preparer.first_name} ${preparer.last_name}` : '-'}
                          </p>
                        </div>
                      </div>

                      {/* Other members */}
                      {otherMembers.length > 0 && (
                        <div className="flex items-center gap-2">
                          <div className="flex gap-1">
                            {otherMembers.slice(0, 3).map((member) => (
                              <UserAvatar key={member.id} user={member} />
                            ))}
                            {otherMembers.length > 3 && (
                              <div className="w-6 h-6 rounded-full bg-brand-100 text-brand-600 text-[10px] flex items-center justify-center font-medium border border-white">
                                +{otherMembers.length - 3}
                              </div>
                            )}
                          </div>
                          <span className="text-[10px] text-slate-500 font-medium">{otherMembers.length} member{otherMembers.length > 1 ? 's' : ''}</span>
                        </div>
                      )}
                    </div>

                    {/* Activity weeks */}
                    <div className="mt-2 pt-2 border-t border-steel-100 flex items-center gap-1 flex-wrap">
                      {Object.entries(team.active_periods).length > 0 ? (
                        <>
                          {Object.entries(team.active_periods)
                            .flatMap(([year, weeks]) =>
                              weeks.slice(0, 4).map((week) => (
                                <span key={`${year}-${week}`} className="px-1.5 py-0.5 text-[10px] bg-steel-100 text-slate-600 rounded">
                                  W{week}
                                </span>
                              ))
                            )}
                          {Object.entries(team.active_periods).flatMap(([_, weeks]) => weeks).length > 4 && (
                            <span className="px-1.5 py-0.5 text-[10px] bg-brand-100 text-brand-600 rounded">
                              +{Object.entries(team.active_periods).flatMap(([_, weeks]) => weeks).length - 4}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-[10px] text-slate-400">No weeks</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </main>
    </div>
  );

};

export default TeamManager;

